import React, { useEffect, useMemo, useState } from 'react'
import {
  IconClipboardList,
  IconHeadphones,
  IconMail,
  IconMessages,
  IconPhoneCall,
  IconUsers,
} from '@tabler/icons-react'
import { useFormik } from 'formik'
import { isEmpty, union } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'

import { Radio, Toggle } from '@/components/forms/Checkbox'
import { Divider } from '@/components/dividers/Divider'
import { apiService, fetchingAPI } from '@/api'
import { toast } from '@/components/toasts/toast.helpers'
import { fetchOrganizations } from '@/reducers/organizations/organizations.actions'
import { Banner } from '@/components/banners/Banner'
import { Button } from '@/components/buttons/Button'
import Select from '@/components/forms/Select'
import { PageHeader } from '@/components/headers/PageHeader'
import { Card } from '@/components/cards/Card'
import { FormField } from '@/components/forms/FormField'
import { TextInput } from '@/components/forms/TextInput'
import { TextArea } from '@/components/forms/TextArea'
import {
  DynamicRowsSection,
  SetupProgressSection,
} from '@/views/Calls/components/InstantDemoPageSections'
import { useInstantDemoSetup } from '@/utils/InstantDemoHelper'
import { getOrganizationOptions } from '@/utils/helpers'

import {
  buildOrganizationResources,
  buildSetupPayload,
  buildSetupValidationSchema,
  createCriteriaRow,
  createFieldRow,
  DISTRIBUTE_OVER_DAYS_MAX,
  DISTRIBUTE_OVER_DAYS_MIN,
  DISTRIBUTION_SHAPES,
  filterUserOptionsByTags,
  getCallExplorerLink,
  getDefaultPlaybookSettings,
  getDefaultScoringSettings,
  getOmnichannelExplorerLink,
  getOrganizationLink,
  INITIAL_FORM_STATE,
  INITIAL_ORG_RESOURCES,
  INITIAL_SETUP_STATE,
  LOCKED_SECTION_MESSAGE,
  OMNICHANNEL_CHANNELS,
  OMNICHANNEL_CONVERSATIONS_MAX,
  OMNICHANNEL_CONVERSATIONS_MIN,
} from './InstantDemoPage.helpers'

const omnichannelChannelOptions = OMNICHANNEL_CHANNELS.map((channel) => ({
  value: channel,
  label: channel.toUpperCase(),
}))

const distributionShapeOptions = DISTRIBUTION_SHAPES.map((shape) => ({
  value: shape,
  label: shape.charAt(0).toUpperCase() + shape.slice(1),
}))

const InstantDemoPage = () => {
  const dispatch = useDispatch()
  const [formError, setFormError] = useState('')
  const [orgResources, setOrgResources] = useState(INITIAL_ORG_RESOURCES)
  const [showPlaybookOptions, setShowPlaybookOptions] = useState(false)
  const [showScoringOptions, setShowScoringOptions] = useState(false)
  const [orgResourcesRefreshKey, setOrgResourcesRefreshKey] = useState(0)
  const organizations = useSelector((state) => state.organizations)
  const { username, organizationid, token } = useSelector((state) => state.currentUser)
  const audioIngestionUrl = new URL(apiService.audioIngestion_websocket)
  const wsProtocol = audioIngestionUrl.protocol === 'http:' ? 'ws:' : 'wss:'
  const wsUrl = `${wsProtocol}//${audioIngestionUrl.host}/generation/setup-organization-ws`

  const { setupState, startSetup, stopSetup, resetSetup } = useInstantDemoSetup({
    INITIAL_SETUP_STATE,
    wsUrl,
  })

  const validationSchema = useMemo(
    () => buildSetupValidationSchema({ showPlaybookOptions, showScoringOptions }),
    [showPlaybookOptions, showScoringOptions]
  )
  const formik = useFormik({
    initialValues: INITIAL_FORM_STATE,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: () => {},
  })
  const { values: formValues, setFieldValue, setValues, resetForm: resetFormikForm } = formik
  const replaceFormValues = (nextValues) => setValues(nextValues, false)

  const organizationOptions = useMemo(() => getOrganizationOptions(organizations), [organizations])
  const flatTagOptions = useMemo(
    () => orgResources.tagOptions.flatMap((group) => group.options || []),
    [orgResources.tagOptions]
  )
  const selectedTagOptions = useMemo(
    () => flatTagOptions.filter((tag) => formValues.userTagIds.includes(tag.value)),
    [flatTagOptions, formValues.userTagIds]
  )
  const selectedExistingAgentFilterTagOptions = useMemo(
    () => flatTagOptions.filter((tag) => formValues.existingAgentFilterTagIds.includes(tag.value)),
    [flatTagOptions, formValues.existingAgentFilterTagIds]
  )
  const selectedAgentOptions = useMemo(
    () =>
      orgResources.userOptions.filter((user) => formValues.selectedUsernames.includes(user.value)),
    [formValues.selectedUsernames, orgResources.userOptions]
  )
  const hasExistingAgentOptions = !isEmpty(orgResources.userOptions)
  const canUseExistingResources = Boolean(formValues.organizationId)
  const hasOrganizationSelection = Boolean(
    formValues.organizationId || formValues.organizationName.trim()
  )
  const shouldShowNotetakerControls =
    formValues.playbookMode === 'create' ||
    (formValues.playbookMode === 'existing' && Boolean(formValues.selectedPlaybookCid))

  useEffect(() => {
    if (!setupState.isComplete) {
      return
    }

    dispatch(fetchOrganizations())

    const completedOrgId = setupState.completionData?.organization_id
    if (completedOrgId && !formValues.organizationId) {
      setFieldValue('organizationId', completedOrgId, false)
    } else {
      setOrgResourcesRefreshKey((prev) => prev + 1)
    }
  }, [
    formValues.organizationId,
    setFieldValue,
    setupState.completionData?.organization_id,
    setupState.isComplete,
  ])

  const updateFormValue = (name, value) => {
    setFieldValue(name, value, false)
  }

  const handleNativeTextChange = (event) => {
    updateFormValue(event.target.name, event.target.value)
  }

  const handleCheckboxChange = (_, { name, checked }) => {
    updateFormValue(name, !!checked)
  }

  const handleNumberChange = (event) => {
    const { name, value } = event.target
    const parsed = parseInt(value, 10)

    if (name === 'newUsersToCreate') {
      updateFormValue(name, Number.isNaN(parsed) ? 0 : Math.max(0, parsed))
      return
    }

    if (name === 'distributeOverDays') {
      updateFormValue(
        name,
        Number.isNaN(parsed)
          ? null
          : Math.min(Math.max(parsed, DISTRIBUTE_OVER_DAYS_MIN), DISTRIBUTE_OVER_DAYS_MAX)
      )
      return
    }

    updateFormValue(name, Number.isNaN(parsed) ? null : Math.min(Math.max(parsed, 1), 100))
  }

  const resetDependentSelections = (organizationId = null, baseValues = formValues) => {
    setOrgResources(INITIAL_ORG_RESOURCES)
    setShowPlaybookOptions(false)
    setShowScoringOptions(false)
    replaceFormValues({
      ...baseValues,
      ...getDefaultPlaybookSettings(),
      ...getDefaultScoringSettings(organizationId),
      agentMode: organizationId ? 'existing' : 'create',
      newUsersToCreate: INITIAL_FORM_STATE.newUsersToCreate,
      selectedUsernames: [],
      userTagIds: [],
      existingAgentFilterTagIds: [],
    })
  }

  const handleOrganizationChange = (option) => {
    if (!option) {
      resetDependentSelections(null, {
        ...formValues,
        organizationId: null,
        organizationName: '',
      })
      return
    }

    const existingOption = organizationOptions.find((orgOption) => orgOption.value === option.value)
    const organizationName = option.label || option.value || ''
    const organizationId = existingOption ? existingOption.value : null

    resetDependentSelections(organizationId, {
      ...formValues,
      organizationId,
      organizationName,
    })
  }

  useEffect(() => {
    if (!formValues.organizationId) {
      setOrgResources(INITIAL_ORG_RESOURCES)
      return undefined
    }

    let cancelled = false

    const loadOrganizationResources = async () => {
      setOrgResources((prev) => ({ ...prev, loading: true }))

      try {
        const [userResponse, playbooks, scorecards, tags, tagCategories] = await Promise.all([
          fetchingAPI(
            `${apiService.core}/api/organizations/${formValues.organizationId}/users`,
            'GET',
            dispatch
          ),
          fetchingAPI(
            `${apiService.core}/api/${formValues.organizationId}/configs?requested_properties=id,name,cid`,
            'GET',
            dispatch
          ),
          fetchingAPI(
            `${apiService.scorecard}/scoring/scorecards/current?requested_organization_id=${formValues.organizationId}&scorecard_type=copilot`,
            'GET',
            dispatch
          ),
          fetchingAPI(
            `${apiService.core}/api/organizations/${formValues.organizationId}/tags`,
            'GET',
            dispatch
          ),
          fetchingAPI(
            `${apiService.core}/api/organizations/${formValues.organizationId}/tags/categories`,
            'GET',
            dispatch
          ),
        ])

        if (cancelled) {
          return
        }

        setOrgResources(
          buildOrganizationResources({
            userResponse,
            playbooks,
            scorecards,
            tags,
            tagCategories,
          })
        )
      } catch (err) {
        if (cancelled) {
          return
        }

        setOrgResources(INITIAL_ORG_RESOURCES)
        toast.error('Failed to load organization resources for Instant Demo.', { cause: err })
      }
    }

    loadOrganizationResources()

    return () => {
      cancelled = true
    }
  }, [dispatch, formValues.organizationId, orgResourcesRefreshKey])

  useEffect(() => {
    if (!formValues.organizationId || orgResources.loading || !orgResources.hasLoaded) {
      return
    }

    if (!hasExistingAgentOptions && formValues.agentMode === 'existing') {
      replaceFormValues({
        ...formValues,
        agentMode: 'create',
        selectedUsernames: [],
        newUsersToCreate: Math.max(formValues.newUsersToCreate, 1),
      })
    }
  }, [
    formValues.agentMode,
    formValues.organizationId,
    formValues.newUsersToCreate,
    hasExistingAgentOptions,
    orgResources.hasLoaded,
    orgResources.loading,
  ])

  const handlePlaybookModeChange = (_, { value }) => {
    replaceFormValues({
      ...formValues,
      playbookMode: value,
      selectedPlaybookCid: '',
      notetakerMode: value === 'existing' ? 'reuse' : 'create',
    })
  }

  const handlePlaybookSelection = (option) => {
    replaceFormValues({
      ...formValues,
      selectedPlaybookCid: option?.value || '',
    })
  }

  const handleScorecardModeChange = (_, { value }) => {
    replaceFormValues({
      ...formValues,
      scorecardMode: value,
      selectedScorecardConfigId: value === 'existing' ? formValues.selectedScorecardConfigId : null,
    })
  }

  const handleScorecardSelection = (option) => {
    updateFormValue('selectedScorecardConfigId', option?.value || null)
  }

  const handlePlaybookOptionsToggle = (_, { checked }) => {
    setShowPlaybookOptions(!!checked)
    if (!checked) {
      replaceFormValues({ ...formValues, ...getDefaultPlaybookSettings() })
    }
  }

  const handleScoringOptionsToggle = (_, { checked }) => {
    setShowScoringOptions(!!checked)
  }

  const extractOptionValues = (options) =>
    Array.isArray(options) ? options.map((option) => option.value) : []

  const handleTagSelection = (options) => {
    updateFormValue('userTagIds', extractOptionValues(options))
  }

  const handleSelectedAgentChange = (options) => {
    updateFormValue('selectedUsernames', extractOptionValues(options))
  }

  const handleExistingAgentTagChange = (options) => {
    const tagIds = extractOptionValues(options)
    const taggedUsernames = isEmpty(tagIds)
      ? []
      : extractOptionValues(filterUserOptionsByTags(orgResources.userOptions, tagIds))
    replaceFormValues({
      ...formValues,
      existingAgentFilterTagIds: tagIds,
      // selecting a tag adds its agents to the selection; fine-tune individuals below
      selectedUsernames: union(formValues.selectedUsernames, taggedUsernames),
    })
  }

  const handleDistributionShapeChange = (option) => {
    updateFormValue('distributionShape', option?.value || 'uniform')
  }

  const selectedOmnichannelChannelOptions = useMemo(
    () =>
      omnichannelChannelOptions.filter((option) =>
        formValues.omnichannelChannels.includes(option.value)
      ),
    [formValues.omnichannelChannels]
  )

  const handleOmnichannelChannelsChange = (options) => {
    updateFormValue('omnichannelChannels', extractOptionValues(options))
  }

  const handleOmnichannelCountChange = (event) => {
    const parsed = parseInt(event.target.value, 10)
    updateFormValue('omnichannelConversationsPerChannel', Number.isNaN(parsed) ? null : parsed)
  }

  const handleAgentModeChange = (_, { value }) => {
    replaceFormValues({
      ...formValues,
      agentMode: value,
      selectedUsernames: value === 'existing' ? formValues.selectedUsernames : [],
      existingAgentFilterTagIds: value === 'existing' ? formValues.existingAgentFilterTagIds : [],
      newUsersToCreate: value === 'create' ? Math.max(formValues.newUsersToCreate, 1) : 0,
      userTagIds: value === 'create' ? formValues.userTagIds : [],
    })
  }

  const handleCollectionRowChange = (collectionName, rowId, fieldName, value) => {
    replaceFormValues({
      ...formValues,
      [collectionName]: formValues[collectionName].map((row) =>
        row.id === rowId ? { ...row, [fieldName]: value } : row
      ),
    })
  }

  const handleNotetakerFieldChange = (rowId, fieldName, value) => {
    handleCollectionRowChange('notetakerFields', rowId, fieldName, value)
  }

  const handleCriteriaChange = (rowId, fieldName, value) => {
    handleCollectionRowChange('scorecardCriteria', rowId, fieldName, value)
  }

  const addCollectionRow = (collectionName, createRow) => {
    replaceFormValues({
      ...formValues,
      [collectionName]: [...formValues[collectionName], createRow()],
    })
  }

  const removeCollectionRow = (collectionName, rowId) => {
    replaceFormValues({
      ...formValues,
      [collectionName]:
        formValues[collectionName].length > 1
          ? formValues[collectionName].filter((row) => row.id !== rowId)
          : formValues[collectionName],
    })
  }

  const addNotetakerField = () => {
    addCollectionRow('notetakerFields', createFieldRow)
  }

  const removeNotetakerField = (rowId) => {
    removeCollectionRow('notetakerFields', rowId)
  }

  const addCriteriaRow = () => {
    addCollectionRow('scorecardCriteria', createCriteriaRow)
  }

  const removeCriteriaRow = (rowId) => {
    removeCollectionRow('scorecardCriteria', rowId)
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setFormError('')

    try {
      const validatedValues = await validationSchema.validate(formValues, { abortEarly: true })
      const payload = buildSetupPayload({
        formValues: validatedValues,
        username,
        organizationid,
        token,
        showPlaybookOptions,
        showScoringOptions,
      })

      startSetup(payload)
    } catch (error) {
      if (error.name === 'ValidationError') {
        setFormError(error.message)
        toast.error(error.message)
        return
      }

      throw error
    }
  }

  const handleResetForm = () => {
    resetFormikForm({ values: INITIAL_FORM_STATE })
    setFormError('')
    setOrgResources(INITIAL_ORG_RESOURCES)
    setShowPlaybookOptions(false)
    setShowScoringOptions(false)
    resetSetup()
  }

  const submitButtonLabel = setupState.isSettingUp
    ? 'Setting Up Organization...'
    : setupState.isComplete
      ? 'Run Again'
      : 'Setup Organization'

  const scoringTooltipContent =
    'Eligible scorecards may still be scored, considering deactivating unwanted scorecards'

  return (
    <>
      <PageHeader title="Instant Demo" />

      <Card>
        <SetupProgressSection
          setupState={setupState}
          onStop={stopSetup}
          getOrganizationLink={getOrganizationLink}
          getCallExplorerLink={getCallExplorerLink}
          getOmnichannelExplorerLink={getOmnichannelExplorerLink}
        />

        {formError && (
          <div className="mb">
            <Banner critical>{formError}</Banner>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="mt">
            <Card
              inlineIcon
              icon={<IconUsers className="icon-svg" size={28} />}
              title="Organization"
            >
              <div className="field-stack">
                <FormField>
                  <label>Organization</label>
                  <Select
                    allowFreeText
                    options={organizationOptions}
                    placeholder="Select existing or enter a new name"
                    value={formValues.organizationId ?? formValues.organizationName}
                    onChange={handleOrganizationChange}
                    isClearable
                    fixedWidth
                    isDisabled={setupState.isSettingUp}
                  />
                </FormField>

                <FormField label="Use case" required>
                  <TextArea
                    name="useCase"
                    placeholder="Describe the primary contact-center use case"
                    value={formValues.useCase}
                    onChange={handleNativeTextChange}
                    rows={3}
                    disabled={setupState.isSettingUp}
                  />
                </FormField>

                <FormField label="Insights Goal">
                  <TextArea
                    name="insightsGoal"
                    placeholder="What should this demo surface for QA, coaching, or leadership?"
                    value={formValues.insightsGoal}
                    onChange={handleNativeTextChange}
                    rows={3}
                    disabled={setupState.isSettingUp}
                  />
                </FormField>
              </div>
            </Card>

            {!hasOrganizationSelection && <Banner informative>{LOCKED_SECTION_MESSAGE}</Banner>}

            {hasOrganizationSelection && (
              <div className="mb">
                <Card
                  inlineIcon
                  icon={<IconHeadphones className="icon-svg" size={28} />}
                  title="Agents"
                >
                  {formValues.generateCalls || formValues.generateOmnichannel ? (
                    <div className="field-stack">
                      <FormField>
                        <Radio
                          label="Use Existing Agents"
                          value="existing"
                          checked={formValues.agentMode === 'existing'}
                          onChange={handleAgentModeChange}
                          disabled={
                            setupState.isSettingUp ||
                            !formValues.organizationId ||
                            !hasExistingAgentOptions
                          }
                        />
                      </FormField>
                      <FormField>
                        <Radio
                          label="Create New Agents"
                          value="create"
                          checked={formValues.agentMode === 'create'}
                          onChange={handleAgentModeChange}
                          disabled={setupState.isSettingUp}
                        />
                      </FormField>

                      {formValues.organizationId &&
                        !orgResources.loading &&
                        isEmpty(orgResources.userOptions) && (
                          <Banner warning>
                            This organization has no existing agents available. Use Create New
                            Agents for this run.
                          </Banner>
                        )}

                      {formValues.agentMode === 'existing' && (
                        <>
                          <FormField>
                            <label>Select Agents by Tag</label>
                            <Select
                              options={orgResources.tagOptions}
                              isMulti
                              placeholder="Select agents by tag"
                              value={selectedExistingAgentFilterTagOptions}
                              onChange={handleExistingAgentTagChange}
                              fixedWidth
                              isDisabled={
                                setupState.isSettingUp ||
                                !formValues.organizationId ||
                                orgResources.loading ||
                                isEmpty(orgResources.tagOptions)
                              }
                            />
                          </FormField>

                          <FormField>
                            <label>Assign Existing Agents</label>
                            <Select
                              options={orgResources.userOptions}
                              isMulti
                              placeholder="Select agents for these interactions"
                              value={selectedAgentOptions}
                              onChange={handleSelectedAgentChange}
                              fixedWidth
                              isDisabled={
                                setupState.isSettingUp ||
                                !formValues.organizationId ||
                                orgResources.loading
                              }
                            />
                          </FormField>
                        </>
                      )}

                      {formValues.agentMode === 'create' && (
                        <>
                          <FormField label="New Users to Create">
                            <TextInput
                              name="newUsersToCreate"
                              type="number"
                              min="0"
                              value={formValues.newUsersToCreate}
                              onChange={handleNumberChange}
                              disabled={setupState.isSettingUp}
                            />
                          </FormField>

                          <FormField>
                            <label>Assign Tags</label>
                            <Select
                              options={orgResources.tagOptions}
                              isMulti
                              placeholder="Select tags for new users"
                              value={selectedTagOptions}
                              onChange={handleTagSelection}
                              fixedWidth
                              isDisabled={
                                setupState.isSettingUp ||
                                !formValues.organizationId ||
                                formValues.newUsersToCreate === 0
                              }
                            />
                          </FormField>

                          {formValues.organizationId && formValues.newUsersToCreate === 0 && (
                            <Banner informative>
                              Tags apply only to newly created users, so tag selection is disabled
                              when the new user count is 0.
                            </Banner>
                          )}
                        </>
                      )}
                    </div>
                  ) : (
                    <Banner informative>
                      Enable Calls or OmniChannel below to assign agents to these interactions.
                    </Banner>
                  )}
                </Card>

                <Card
                  inlineIcon
                  icon={<IconPhoneCall className="icon-svg" size={28} />}
                  title="Calls"
                >
                  <div className="field-stack">
                    <FormField>
                      <Toggle
                        label="Generate Calls"
                        name="generateCalls"
                        checked={formValues.generateCalls}
                        onChange={handleCheckboxChange}
                        disabled={setupState.isSettingUp}
                      />
                    </FormField>

                    {formValues.generateCalls ? (
                      <>
                        <FormField label="Number of Calls">
                          <TextInput
                            name="numCalls"
                            type="number"
                            min="1"
                            max="100"
                            value={formValues.numCalls}
                            onChange={handleNumberChange}
                            disabled={setupState.isSettingUp}
                          />
                        </FormField>

                        <FormField>
                          <Toggle
                            label="Fast mode (quicker setup, lower-fidelity audio)"
                            name="fastMode"
                            checked={formValues.fastMode}
                            onChange={handleCheckboxChange}
                            disabled={setupState.isSettingUp}
                          />
                        </FormField>

                        <FormField>
                          <Toggle
                            label="Simulate a trend over time"
                            name="simulateTrend"
                            checked={formValues.simulateTrend}
                            onChange={handleCheckboxChange}
                            disabled={setupState.isSettingUp}
                          />
                        </FormField>

                        {formValues.simulateTrend && (
                          <>
                            <FormField label="Distribute Over (days)">
                              <TextInput
                                name="distributeOverDays"
                                type="number"
                                min={DISTRIBUTE_OVER_DAYS_MIN}
                                max={DISTRIBUTE_OVER_DAYS_MAX}
                                value={formValues.distributeOverDays}
                                onChange={handleNumberChange}
                                disabled={setupState.isSettingUp}
                              />
                            </FormField>

                            <FormField>
                              <label>Distribution Shape</label>
                              <Select
                                options={distributionShapeOptions}
                                value={formValues.distributionShape}
                                onChange={handleDistributionShapeChange}
                                fixedWidth
                                isDisabled={setupState.isSettingUp}
                              />
                            </FormField>

                            <Banner informative>
                              Calls and omnichannel interactions are spread across the last{' '}
                              {formValues.distributeOverDays} days and a matching Trend is created.
                              Trends look back up to {DISTRIBUTE_OVER_DAYS_MAX} days; the Trend
                              takes a few minutes to populate after setup completes.
                            </Banner>
                          </>
                        )}
                      </>
                    ) : (
                      <Banner informative>Calls will not be generated for this setup</Banner>
                    )}
                  </div>
                </Card>

                <Card
                  inlineIcon
                  icon={<IconMail className="icon-svg" size={28} />}
                  title="OmniChannel"
                >
                  <div className="field-stack">
                    <FormField>
                      <Toggle
                        label="Generate OmniChannel Data"
                        name="generateOmnichannel"
                        checked={formValues.generateOmnichannel}
                        onChange={handleCheckboxChange}
                        disabled={setupState.isSettingUp}
                      />
                    </FormField>

                    {formValues.generateOmnichannel ? (
                      <>
                        <FormField>
                          <label>Channels</label>
                          <Select
                            options={omnichannelChannelOptions}
                            isMulti
                            placeholder="Select channels (chat, sms, email)"
                            value={selectedOmnichannelChannelOptions}
                            onChange={handleOmnichannelChannelsChange}
                            fixedWidth
                            isDisabled={setupState.isSettingUp}
                          />
                        </FormField>

                        <FormField label="Conversations per channel">
                          <TextInput
                            name="omnichannelConversationsPerChannel"
                            type="number"
                            min={OMNICHANNEL_CONVERSATIONS_MIN}
                            max={OMNICHANNEL_CONVERSATIONS_MAX}
                            value={formValues.omnichannelConversationsPerChannel ?? ''}
                            onChange={handleOmnichannelCountChange}
                            disabled={setupState.isSettingUp}
                          />
                        </FormField>
                      </>
                    ) : (
                      <Banner informative>
                        OmniChannel demo data will not be generated for this setup
                      </Banner>
                    )}
                  </div>
                </Card>

                <Card
                  inlineIcon
                  icon={<IconMessages className="icon-svg" size={28} />}
                  title="Playbook & Notetaker"
                >
                  <div className="field-stack">
                    <FormField>
                      <Toggle
                        label="Customize Playbook & Notetaker"
                        checked={showPlaybookOptions}
                        onChange={handlePlaybookOptionsToggle}
                        disabled={setupState.isSettingUp}
                      />
                    </FormField>

                    {showPlaybookOptions ? (
                      <>
                        <FormField>
                          <Radio
                            label="Create new playbook"
                            value="create"
                            checked={formValues.playbookMode === 'create'}
                            onChange={handlePlaybookModeChange}
                            disabled={setupState.isSettingUp}
                          />
                        </FormField>
                        <FormField>
                          <Radio
                            label="Use existing playbook"
                            value="existing"
                            checked={formValues.playbookMode === 'existing'}
                            onChange={handlePlaybookModeChange}
                            disabled={setupState.isSettingUp || !canUseExistingResources}
                          />
                        </FormField>

                        {formValues.playbookMode === 'create' && (
                          <FormField label="Playbook Name">
                            <TextInput
                              name="playbookName"
                              placeholder="e.g. Acme Sales Playbook"
                              value={formValues.playbookName}
                              onChange={handleNativeTextChange}
                              disabled={setupState.isSettingUp}
                            />
                          </FormField>
                        )}

                        {formValues.playbookMode === 'existing' && (
                          <>
                            <FormField>
                              <label>Existing playbook</label>
                              <Select
                                options={orgResources.playbooks}
                                placeholder="Select playbook"
                                value={formValues.selectedPlaybookCid}
                                onChange={handlePlaybookSelection}
                                isClearable
                                fixedWidth
                                loading={orgResources.loading}
                                isDisabled={setupState.isSettingUp || orgResources.loading}
                              />
                            </FormField>

                            {!orgResources.loading &&
                              canUseExistingResources &&
                              isEmpty(orgResources.playbooks) && (
                                <Banner warning>
                                  This organization has no active playbooks to reuse.
                                </Banner>
                              )}
                          </>
                        )}

                        {shouldShowNotetakerControls && (
                          <>
                            <Divider />
                            <h4>Notetaker</h4>

                            {formValues.playbookMode === 'existing' &&
                              formValues.selectedPlaybookCid && (
                                <FormField>
                                  <Toggle
                                    label="Update notetaker"
                                    checked={formValues.notetakerMode === 'create'}
                                    onChange={(_, { checked }) =>
                                      updateFormValue('notetakerMode', checked ? 'create' : 'reuse')
                                    }
                                    disabled={
                                      setupState.isSettingUp || !formValues.selectedPlaybookCid
                                    }
                                  />
                                </FormField>
                              )}

                            {formValues.notetakerMode === 'create' && (
                              <DynamicRowsSection
                                title="Notetaker Fields"
                                autoDecideLabel="Auto decide notetaker fields"
                                autoDecideName="notetakerAutoDecide"
                                autoDecideValue={formValues.notetakerAutoDecide}
                                handleAutoDecideChange={handleCheckboxChange}
                                rows={formValues.notetakerFields}
                                rowLabel="Field"
                                firstFieldLabel="Field Name"
                                firstFieldKey="fieldName"
                                secondFieldLabel="Prompt / Instruction"
                                secondFieldKey="prompt"
                                handleRowFieldChange={handleNotetakerFieldChange}
                                handleAddRow={addNotetakerField}
                                handleRemoveRow={removeNotetakerField}
                                disableRemove={formValues.notetakerFields.length === 1}
                                isDisabled={setupState.isSettingUp}
                              />
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <Banner informative>
                        Playbooks &amp; Notetaker will not be generated for this setup
                      </Banner>
                    )}
                  </div>
                </Card>

                <Card
                  inlineIcon
                  icon={<IconClipboardList className="icon-svg" size={28} />}
                  title="Scoring"
                  info={scoringTooltipContent}
                >
                  <div className="field-stack">
                    <FormField>
                      <Toggle
                        label="Customize Scoring"
                        checked={showScoringOptions}
                        onChange={handleScoringOptionsToggle}
                        disabled={setupState.isSettingUp}
                      />
                    </FormField>

                    {showScoringOptions ? (
                      <>
                        <FormField>
                          <Radio
                            label="Use existing Quality scorecard"
                            value="existing"
                            checked={formValues.scorecardMode === 'existing'}
                            onChange={handleScorecardModeChange}
                            disabled={setupState.isSettingUp || !canUseExistingResources}
                          />
                        </FormField>
                        <FormField>
                          <Radio
                            label="Create new Quality scorecard"
                            value="create"
                            checked={formValues.scorecardMode === 'create'}
                            onChange={handleScorecardModeChange}
                            disabled={setupState.isSettingUp}
                          />
                        </FormField>
                        <FormField>
                          <Radio
                            label="Skip explicit scorecard selection"
                            value="all_active"
                            checked={formValues.scorecardMode === 'all_active'}
                            onChange={handleScorecardModeChange}
                            disabled={setupState.isSettingUp}
                          />
                        </FormField>

                        {formValues.scorecardMode === 'existing' && (
                          <>
                            <FormField>
                              <label>Existing Quality scorecard</label>
                              <Select
                                options={orgResources.scorecards}
                                placeholder="Select scorecard"
                                value={formValues.selectedScorecardConfigId}
                                onChange={handleScorecardSelection}
                                isClearable
                                fixedWidth
                                loading={orgResources.loading}
                                isDisabled={setupState.isSettingUp || orgResources.loading}
                              />
                            </FormField>

                            {!orgResources.loading &&
                              canUseExistingResources &&
                              isEmpty(orgResources.scorecards) && (
                                <Banner warning>
                                  This organization has no Quality scorecards to reuse.
                                </Banner>
                              )}
                          </>
                        )}

                        {formValues.scorecardMode === 'create' && (
                          <>
                            <FormField label="Scorecard Name">
                              <TextInput
                                name="scorecardName"
                                placeholder="e.g. Acme QA Scorecard"
                                value={formValues.scorecardName}
                                onChange={handleNativeTextChange}
                                disabled={setupState.isSettingUp}
                              />
                            </FormField>
                            <DynamicRowsSection
                              title="Scorecard Criteria"
                              autoDecideLabel="Auto decide criteria and questions"
                              autoDecideName="scorecardAutoDecide"
                              autoDecideValue={formValues.scorecardAutoDecide}
                              handleAutoDecideChange={handleCheckboxChange}
                              rows={formValues.scorecardCriteria}
                              rowLabel="Criterion"
                              firstFieldLabel="Criteria Name"
                              firstFieldKey="criteriaName"
                              secondFieldLabel="Question"
                              secondFieldKey="question"
                              handleRowFieldChange={handleCriteriaChange}
                              handleAddRow={addCriteriaRow}
                              handleRemoveRow={removeCriteriaRow}
                              disableRemove={formValues.scorecardCriteria.length === 1}
                              isDisabled={setupState.isSettingUp}
                            />
                          </>
                        )}
                      </>
                    ) : (
                      <Banner informative>
                        Leave this off to use the default scoring behavior for the selected
                        organization
                      </Banner>
                    )}
                  </div>
                </Card>
              </div>
            )}

            <Button
              primary
              type="submit"
              loading={setupState.isSettingUp || setupState.connectionStatus === 'connecting'}
              disabled={setupState.isSettingUp}
            >
              {submitButtonLabel}
            </Button>

            {setupState.isComplete && (
              <Button className="ml" type="button" onClick={handleResetForm}>
                Setup Another Organization
              </Button>
            )}
          </div>
        </form>
      </Card>
    </>
  )
}

export default InstantDemoPage
